import { CheckCircle } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import type { SubscriptionTier } from '@maker-toolchest/shared';

interface CancelReversalModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  currentTier: SubscriptionTier;
  loading?: boolean;
}

const TIER_NAMES: Record<SubscriptionTier, string> = {
  TINKERER: 'Tinkerer',
  HOBBYIST: 'Hobbyist',
  BUSINESS: 'Business',
};

const TIER_PRICES: Record<SubscriptionTier, string> = {
  TINKERER: 'Free',
  HOBBYIST: '$12/month',
  BUSINESS: '$49/month',
};

export function CancelReversalModal({
  open,
  onClose,
  onConfirm,
  currentTier,
  loading = false,
}: CancelReversalModalProps) {
  return (
    <Modal open={open} onClose={onClose} size="md">
      <div className="flex flex-col gap-6">
        {/* Header */}
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success/10">
            <CheckCircle className="h-6 w-6 text-success" />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold text-foreground">
              Keep {TIER_NAMES[currentTier]} Plan
            </h2>
            <p className="mt-1 text-sm text-foreground-muted">
              Your subscription will continue and you'll keep all your current features.
            </p>
          </div>
        </div>

        {/* Confirmation Info */}
        <div className="rounded-lg border border-success bg-success/5 p-4">
          <p className="text-sm text-foreground">
            <strong>Your {TIER_NAMES[currentTier]} plan will continue.</strong>
          </p>
          <p className="mt-2 text-sm text-foreground-muted">
            You'll be charged {TIER_PRICES[currentTier]} at your next billing cycle and retain full
            access to all features.
          </p>
        </div>

        {/* Actions */}
        <div className="flex items-center justify-end gap-3 border-t border-border pt-4">
          <Button variant="ghost" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={onConfirm}
            loading={loading}
          >
            Keep My Plan
          </Button>
        </div>
      </div>
    </Modal>
  );
}
